import { useMemo, useState, useContext, useEffect } from 'react'
import styled from '@emotion/styled'
import { Typography } from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import FormControlLabel from '@mui/material/FormControlLabel'
import TextField from '@mui/material/TextField'
import uniqBy from 'lodash/uniqBy'
import { object, func, bool } from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import * as TemplateActions from '@admin/templates/edit-template/template.actions'
import { CvsAutoComplete } from '@components'
import { listContext } from '@contexts'
import { api } from 'api'
import {
  isSplitGovernanceTypeForDestination,
  belongsToPattern,
} from 'field-governance/split-actions'
import { SplitText } from 'field-governance/split-text'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useSsoEnabled } from '@global/hooks/queries/useAuthHintQuery'
import { ListType } from '@admin/template-elements/edit-list/types'

const { DrillDown, Dependent, DropDown } = ListType

const Center = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 8px 0px;
`

export const List = ({ form = {}, setForm = () => {}, disabled = false }) => {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(true)
  const [listValues, setListValues] = useState(null)
  const { byId, fetchLists } = useContext(listContext)
  const ssoEnabled = useSsoEnabled()

  useEffect(() => {
    setLoading(true)
    fetchLists().then(() => {
      setLoading(false)
    })
  }, [fetchLists])

  let { list_governance, apply_default_value_to_empty_values = false } = form

  list_governance = list_governance || { lists: {} }

  const type = useMemo(
    () => !loading && list_governance?.lists?.id && byId[list_governance.lists.id].type,
    [list_governance, byId, loading],
  )

  const firstColumnSelected =
    !loading &&
    list_governance?.list_column_id &&
    byId[list_governance.lists?.id]?.list_columns[0].id === list_governance.list_column_id

  const shouldShowMultiple =
    !loading &&
    (((type === DrillDown || type === Dependent) && firstColumnSelected) || type === DropDown)

  const shouldShowColumnSelect =
    !loading && (type === Dependent || type === DrillDown) && list_governance.lists?.id

  const shouldShowHierarchy =
    !loading && list_governance?.lists?.id && type === DrillDown && firstColumnSelected

  // has to be a template field. And has to be the first column of a drill down.
  const tf = useSelector(state => state.Template.templateFieldsById[form.template_field_id])
  const shouldShowDefaultValue = tf && type === DrillDown

  if (shouldShowDefaultValue && !listValues) {
    setListValues([])
    api.get(`/api/v1/account/lists/${list_governance.lists?.id}`).then(({ data }) => {
      // grab unique values from the first columns
      const values = uniqBy(
        data.list_columns
          .find(c => c.id === list_governance.list_column_id)
          .list_values.map(lv => {
            return { label: lv.value, value: lv.value }
          }),
        item => item.value,
      )
      setListValues(values)
    })
  }

  /**
   * Get the first list_column to set the column_list_id value.
   * - Necessary for validation to pass on a Pattern Variable, since
   * no column is selected by the user, we need to set it dynamically.
   * @param {object} args
   * @param {number} args.value The select value that is the columns key.
   * @returns The list_column_id value.
   */
  const getDropdownColumnId = ({ value }) => {
    const listColumns = byId[value].list_columns
    if (listColumns.length !== 1) return null
    return listColumns[0].id
  }

  return loading ? (
    <Center>
      <CircularProgress color="primary" />
    </Center>
  ) : (
    <>
      <div>
        <CvsAutoComplete
          name="list"
          margin="dense"
          disabled={disabled}
          label={t`Select list`}
          required={true}
          options={Object.keys(byId).map(key => ({
            value: byId[key].id,
            label: byId[key].name,
          }))}
          onSelected={value =>
            setForm(prev => ({
              ...prev,
              list_governance: {
                ...prev.list_governance,
                list_id: value,
                list_column_id: getDropdownColumnId({ value }),
                lists: byId[value],
              },
            }))
          }
          selected={list_governance.lists?.id || ''}
        />
        {Boolean(list_governance.lists?.id) && (
          <span style={{ textAlign: 'right', display: 'flex', justifyContent: 'end' }}>
            <Trans>
              <Link
                to={`/admin/lists/edit/${list_governance.lists?.id}${
                  !ssoEnabled ? '?login=system' : ''
                }`}
                target="_blank"
                title={t`Go to list details page.`}
                css={{
                  textDecoration: 'underline',
                  color: '#0091FF',
                  marginRight: '3px',
                }}
              >
                Go to the list
              </Link>{' '}
              for more information.
            </Trans>
          </span>
        )}
      </div>
      {shouldShowColumnSelect && (
        <CvsAutoComplete
          name="column"
          margin="dense"
          label={t`Select column`}
          disabled={disabled}
          required
          options={byId[list_governance.lists?.id].list_columns.map(c => ({
            label: c.name,
            value: c.id,
          }))}
          onSelected={value => {
            setForm(prev => ({
              ...prev,
              list_governance: { ...prev.list_governance, list_column_id: value },
            }))
          }}
          selected={list_governance.list_column_id}
        />
      )}
      {shouldShowMultiple && (
        <>
          <FormControlLabel
            name="multiple-values"
            margin="dense"
            disabled={disabled}
            control={
              <Checkbox
                onChange={({ target: { checked } }) =>
                  setForm(prev => ({
                    ...prev,
                    list_governance: { ...prev.list_governance, multi: checked },
                  }))
                }
                checked={Boolean(list_governance.multi)}
              />
            }
            label={t`Select multiple values`}
          />
          {!list_governance.multi ? null : (
            <>
              <TextField
                margin="dense"
                variant="outlined"
                name="delimiter"
                disabled={disabled}
                required={list_governance.multi}
                placeholder={t`Delimiter`}
                label={t`Delimiter`}
                value={list_governance.delimiter || ''}
                onChange={({ target: { value } }) =>
                  setForm(prev => ({
                    ...prev,
                    list_governance: { ...prev.list_governance, delimiter: value },
                  }))
                }
              />
              <Typography variant="caption">
                <Trans>
                  It is not recommended to use a single space as a delimiter unless you are certain
                  no spaces exist in your list options. Please use a unique value that does not
                  exist in any of your options.
                </Trans>
              </Typography>
            </>
          )}
        </>
      )}
      {shouldShowHierarchy && (
        <>
          <FormControlLabel
            name="hierarchy"
            margin="dense"
            control={
              <Checkbox
                disabled={disabled}
                onChange={({ target: { checked } }) =>
                  setForm(prev => ({
                    ...prev,
                    list_governance: { ...prev.list_governance, hierarchy: checked },
                  }))
                }
                checked={Boolean(list_governance.hierarchy)}
              />
            }
            label={t`Select value as hierarchy`}
          />
          {!list_governance.hierarchy ? null : (
            <>
              <TextField
                name="hierarchy-indicator"
                margin="dense"
                variant="outlined"
                disabled={disabled}
                required={list_governance.hierarchy}
                placeholder={t`Indicator`}
                value={list_governance.indicator || ''}
                onChange={({ target: { value } }) =>
                  setForm(prev => ({
                    ...prev,
                    list_governance: { ...prev.list_governance, indicator: value },
                  }))
                }
                label={t`Indicator`}
              />
              <Typography variant="caption">
                <Trans>
                  Used to denote relationship between list columns in a single field. (E.G. Country
                  &gt; State &gt; City)
                </Trans>
              </Typography>
            </>
          )}
        </>
      )}
      {shouldShowDefaultValue && (
        <div>
          <CvsAutoComplete
            margin="dense"
            disabled={!listValues || !listValues.length}
            showSpinner={!listValues || !listValues.length}
            label={t`Default value`}
            isClearable={true}
            options={listValues}
            onSelected={value => {
              const newTf = {
                ...tf,
                default_value: value,
              }
              dispatch(TemplateActions.updateTemplateField(newTf))
            }}
            selected={tf.default_value}
          />
          <FormControlLabel
            name="apply-default-value-to-empty-values"
            margin="dense"
            disabled={false}
            control={
              <Checkbox
                onChange={({ target: { checked } }) =>
                  setForm(prev => ({
                    ...prev,
                    apply_default_value_to_empty_values: checked,
                    visible_on_form: checked,
                  }))
                }
                checked={Boolean(apply_default_value_to_empty_values)}
              />
            }
            label={t`Apply default value only to empty values`}
          />
        </div>
      )}
      {isSplitGovernanceTypeForDestination(form.governance_type) && !belongsToPattern(form) ? (
        <SplitText form={form} setForm={setForm} disabled={disabled} />
      ) : null}
    </>
  )
}

List.propTypes = {
  form: object,
  setForm: func,
  disabled: bool,
}
